<template>
  <div class="my-2 rounded-lg border">
    <div class="my-2 grid grid-cols-1 items-center gap-4 p-2 sm:grid-cols-6">
      <StdPropEditor
        v-for="prop in intercomIntegrationProps"
        :key="prop.name"
        :prop-def="prop"
        :class="[getPropLayoutClasses(prop)]"
        :model-value="configurationData[prop.name]"
        @update:model-value="(newValue) => onUpdateProp(prop, newValue)"
      />
      <div v-if="settingsStore.intercomIntegrationEnabled" class="sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3">
        <label class="block text-sm font-medium leading-6 text-gray-900">Ticket Comment Author</label>
        <div class="mt-2 flex items-center space-x-2">
          <Autocomplete
            ref="adminAutocomplete"
            v-model="selectedAdmin"
            :suggestions="filteredAdmins"
            field="name"
            placeholder="Type to search admin"
            class="flex-1"
            input-class="block w-full flex-1 rounded-l-md border-0 px-2 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
            :loading="loadingAdmins"
            :complete-on-focus="true"
            @clear="searchAdmin"
            @complete="searchAdmin"
          >
            <template #option="{ option }">
              <div class="flex items-center">
                <span class="font-medium">{{ option.name }}</span>
                <span class="ml-2 text-sm text-gray-400">ID: {{ option.id }}, Email: {{ option.email }}</span>
              </div>
            </template>
          </Autocomplete>
          <Button v-tooltip.top="'Clear'" :disabled="loadingAdmins" icon="pi pi-times" severity="secondary" @click="selectedAdmin = null" />
        </div>
      </div>
    </div>
    <Message v-if="errorMessage !== ''" class="m-2" severity="error" :sticky="false">{{ errorMessage }}</Message>
    <ProgressDialog ref="integrationsOpProgress" />
    <div class="m-2 flex items-center justify-end space-x-3">
      <button type="button" class="mt-3 inline-flex justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0" @click="onCancel">
        Cancel
      </button>
      <button type="button" class="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500" @click="onSave">Save</button>
    </div>
  </div>
</template>
<script setup>
  import { onMounted, reactive, ref, watch } from 'vue';
  import StdPropEditor from '@/components/Editors/StdPropEditor.vue';
  import { useApi } from '@/hooks/useApi';
  import { useSettingsStore } from '@/store/SettingsStore';
  import Message from 'primevue/message';
  import { useToast } from 'vue-toastification';
  import ProgressDialog from '@/components/Dialogs/ProgressDialog.vue';
  import Autocomplete from 'primevue/autocomplete';
  import Button from 'primevue/button';

  const emit = defineEmits(['canceled', 'saved']);

  const settingsStore = useSettingsStore();

  const api = useApi();
  const errorMessage = ref('');
  const integrationsOpProgress = ref(null);
  const toast = useToast();

  let configurationData = reactive({
    access_token: '',
    token_type: 'bearer',
    ticketCommentAuthorId: '',
  });

  const admins = ref([]);
  const filteredAdmins = ref([]);
  const selectedAdmin = ref(null);
  const loadingAdmins = ref(false);
  const adminAutocomplete = ref(null);

  async function loadAdmins() {
    if (admins.value.length) {
      return admins.value;
    }

    loadingAdmins.value = true;
    const result = await api.intercomGetAdmins();
    if (result.success) {
      loadingAdmins.value = false;
      admins.value = result.data.admins;
    }

    return admins.value;
  }

  async function searchAdmin(event) {
    // if the user hasn’t typed anything yet, use the selectedAdmin’s name
    const rawQuery = event?.query?.trim();
    const query = rawQuery ? rawQuery.toLowerCase() : selectedAdmin.value?.name?.toLowerCase() || '';

    const all = await loadAdmins();
    if (!query) {
      filteredAdmins.value = [...all];
    } else {
      filteredAdmins.value = all.filter((admin) => {
        const name = admin.name.toLowerCase();
        const email = (admin.email || '').toLowerCase();
        const idText = String(admin.id).toLowerCase();
        return name.includes(query) || email.includes(query) || idText.includes(query);
      });
    }
  }

  watch(selectedAdmin, async (newVal) => {
    if (newVal === null) {
      try {
        await settingsStore.setIntegrationSettings('intercom', {
          ticketCommentAuthorId: '',
        });
        toast.success(`Ticket Comment Author Cleared`);
        onUpdateProp({ name: 'ticketCommentAuthorId' }, '');
      } catch (error) {
        console.error('Error clearing ticket comment author:', error);
        toast.error('Failed to clear ticket comment author');
      }
    } else if (typeof newVal === 'object' && newVal?.id && newVal.id !== configurationData.ticketCommentAuthorId) {
      try {
        await settingsStore.setIntegrationSettings('intercom', {
          ticketCommentAuthorId: newVal.id,
        });
        toast.success(`Ticket Comment Author Saved`);
        onUpdateProp({ name: 'ticketCommentAuthorId' }, newVal.id);
      } catch (error) {
        console.error('Error saving ticket comment author:', error);
        toast.error('Failed to save ticket comment author');
      }
    }
  });

  const intercomIntegrationProps = [{ name: 'access_token', type: 'string', title: 'API key', default: '', passwordToggle: true }];

  const propLayoutClassesByType = {
    text: 'col-span-full',
    boolean: 'col-span-full',
    number: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3',
    string: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3',
    select: 'sm:col-span-full md:col-span-full lg:col-span-3 xl:col-span-3',
    stringLong: 'col-span-full',
    audioFile: 'col-span-full',
    transcriptsCollection: 'col-span-full',
    recordingsCollection: 'col-span-full',
    kpiCollection: 'col-span-full',
    categoriesCollection: 'col-span-full',
    temperature: 'col-span-full',
  };

  function getPropLayoutClasses(prop) {
    return propLayoutClassesByType[prop?.type] || '';
  }

  function onUpdateProp(prop, newValue) {
    configurationData[prop.name] = newValue;
  }

  function validateConfiguration() {
    if (!configurationData.access_token || configurationData.access_token === '') {
      return {
        valid: false,
        message: 'Please provide value for API Key',
      };
    }

    return { valid: true };
  }

  async function onSave() {
    errorMessage.value = '';
    const validateResult = validateConfiguration();
    if (!validateResult.valid) {
      errorMessage.value = validateResult.message;
      return;
    }
    const api = useApi();

    integrationsOpProgress.value.show({
      title: 'Enable intercom Integration',
      description: `Validating API Key`,
    });

    // Validate API Key and provision account
    let result = await api.intercomValidateAPIKey(configurationData);
    if (!result.success) {
      integrationsOpProgress.value.showError({ message: `${result.error}` });
      return;
    }
    integrationsOpProgress.value.hide();
    const updateResult = await settingsStore.setIntegrationSettings('intercom', configurationData);
    if (!updateResult) {
      errorMessage.value = `Error updating settings`;
      return;
    }
    await settingsStore.setIntegrationEnabled('intercom', true);

    toast.success(`Intercom Integration Enabled`);
  }

  function onCancel() {
    emit('canceled');
  }

  async function resolveAdmin() {
    const adminId = configurationData.ticketCommentAuthorId;
    if (adminId) {
      try {
        loadingAdmins.value = true;
        const result = await api.intercomGetAdminById({ adminId });
        if (result.success) {
          selectedAdmin.value = { id: adminId, name: result.admin.name };
        } else {
          selectedAdmin.value = { id: adminId, name: `Cannot find admin, admin id: ${adminId}` };
        }
      } catch (error) {
        console.error('Error resolving admin:', error);
        selectedAdmin.value = { id: adminId, name: `Error loading admin: ${adminId}` };
      } finally {
        loadingAdmins.value = false;
      }
    }
  }

  onMounted(async () => {
    const config = await settingsStore.getIntegrationSettings('intercom');
    if (config && typeof config === 'object') {
      Object.keys(config).map((x) => {
        configurationData[x] = config[x];
      });
      await resolveAdmin();
    }
  });
</script>